/** Deep enough for real back-and-forths; the thread view never indents past
 * two reply levels (deeper replies open as their own focused thread). */
export const MAX_COMMENT_DEPTH = 16;

export type CommentThreadPosition = {
  parentCommentId: number | null;
  rootCommentId: number | null;
  depth: number;
};

export type CommentThreadParent = {
  id: number;
  rootCommentId: number | null;
  depth: number;
};

export type CommentThreadEntry = {
  id: number;
  parentCommentId: number | null;
};

type CommentPage<TComment> = {
  comments: readonly TComment[];
};

/**
 * Calculates the only valid position for a new root or direct child. Returning
 * null means the requested child would exceed the supported nesting depth.
 */
export function deriveCommentThreadPosition(
  parent: null,
): CommentThreadPosition;
export function deriveCommentThreadPosition(
  parent: CommentThreadParent,
): CommentThreadPosition | null;
export function deriveCommentThreadPosition(
  parent: CommentThreadParent | null,
): CommentThreadPosition | null {
  if (!parent) {
    return { parentCommentId: null, rootCommentId: null, depth: 0 };
  }

  const parentHasValidIdentity =
    Number.isSafeInteger(parent.id) && parent.id > 0;
  const parentHasValidDepth =
    Number.isSafeInteger(parent.depth) &&
    parent.depth >= 0 &&
    parent.depth <= MAX_COMMENT_DEPTH;
  const parentHasValidRoot =
    parent.depth === 0
      ? parent.rootCommentId === null
      : parent.rootCommentId !== null &&
        Number.isSafeInteger(parent.rootCommentId) &&
        parent.rootCommentId > 0;

  if (!parentHasValidIdentity || !parentHasValidDepth || !parentHasValidRoot) {
    return null;
  }

  const depth = parent.depth + 1;
  if (depth > MAX_COMMENT_DEPTH) return null;

  return {
    parentCommentId: parent.id,
    rootCommentId: parent.rootCommentId ?? parent.id,
    depth,
  };
}

/**
 * Mirrors the server-owned thread calculation for an optimistic row. If its
 * parent is not in the current cache, no optimistic row is created; the server
 * result will arrive through normal invalidation without fabricating ancestry.
 */
export function deriveOptimisticCommentThreadPosition<
  TComment extends CommentThreadParent,
>(
  parentCommentId: number | null | undefined,
  pages: readonly CommentPage<TComment>[] | undefined,
): CommentThreadPosition | null {
  if (parentCommentId == null) return deriveCommentThreadPosition(null);

  const parent = pages
    ?.flatMap((page) => page.comments)
    .find((comment) => comment.id === parentCommentId);

  return parent ? deriveCommentThreadPosition(parent) : null;
}

let optimisticCommentSequence = 0;

/** Generates a unique, server-invalid numeric id for an optimistic comment. */
export function createOptimisticCommentId(now = Date.now()): number {
  optimisticCommentSequence = (optimisticCommentSequence + 1) % 1_000;
  return -(now * 1_000 + optimisticCommentSequence);
}

/** Returns a comment and every cached descendant removed by a cascading delete. */
export function getCommentSubtreeIds(
  comments: readonly CommentThreadEntry[],
  commentId: number,
): Set<number> {
  const childrenByParent = new Map<number, number[]>();
  for (const comment of comments) {
    if (comment.parentCommentId === null) continue;
    const children = childrenByParent.get(comment.parentCommentId) ?? [];
    children.push(comment.id);
    childrenByParent.set(comment.parentCommentId, children);
  }

  const subtreeIds = new Set<number>();
  const pending = [commentId];
  while (pending.length > 0) {
    const id = pending.pop();
    if (id === undefined || subtreeIds.has(id)) continue;
    subtreeIds.add(id);
    pending.push(...(childrenByParent.get(id) ?? []));
  }
  return subtreeIds;
}
